import {
  Alert,
  AlertColor,
  Box,
  Snackbar,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import React from "react";
import {
  Button,
  Container,
  Content,
  ContentBody,
  ContentInput,
  ContentInputPassword,
  ContentInputProfilePhoto,
  ContentInputProfilePhotoIcon,
  ContentTitle,
  Title,
} from "./styles";
import { Select, Option } from "@mui/joy";
import TranslateIcon from "@mui/icons-material/Translate";
import { useNavigate, useParams } from "react-router-dom";
import { UserPermissions } from "../../../App";

interface Props {
  userPermissions: UserPermissions;
}
const Users: React.FC<Props> = ({ userPermissions }) => {
  const navigate = useNavigate();
  let { username } = useParams();

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [language, setLanguage] = React.useState("");

  const [birthDate, setBirthDate] = React.useState<Date>(new Date());
  const [logo, setLogo] = React.useState<string>("");
  const hiddenLogoInput = React.useRef<HTMLInputElement>(null);

  const onImageChangeClick = () => {
    if (hiddenLogoInput.current) {
      hiddenLogoInput.current.click();
    }
  };
  const onHiddenLogoInputChange = (event: any) => {
    const fileUploaded = event.target.files[0];

    var reader = new FileReader();
    var baseString;
    reader.onloadend = function () {
      baseString = reader.result;
      setLogo(baseString + "");
    };
    reader.readAsDataURL(fileUploaded);
  };

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");

  const getUser = () => {
    fetch("/api/v1/patient/get", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setEmail(data.email);
        setBirthDate(new Date(data.birthDate));
        setLogo(data.logo);
        setLanguage(data.language);
      });
  };

  React.useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showStatus, setShowStatus] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState("");
  const [statusSeverity, setStatusSeverity] =
    React.useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  const onEditUser = () => {
    if (password !== confirmPassword) {
      console.log("Not created: Password and its confirmation do not match");
      setStatusMsg(
        `Patient info was not updated (password and confirmation password are not the same). Please try again.`
      );
      setStatusSeverity('error');
      setShowStatus(true);
      return;
    }

    const bodyJson = {
      username,
      logo,
      firstName,
      lastName,
      language,
      birthDate: +birthDate,
      password: password !== "" ? password : undefined,
    };

    fetch("/api/v1/patient/edit", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bodyJson),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          console.log("Created!");
          setStatusMsg("User info was updated! Redirecting in 5 seconds...");
          setStatusSeverity("success");
          setShowStatus(true);
          setTimeout(() => {
            navigate(process.env.PUBLIC_URL + "/Patients");
          }, 5000);
        } else {
          console.log(bodyJson);
          console.log("Not updated: ", data.reason);
          setStatusMsg(
            `User info was not updated (${data.reason + ""
            }). Refresh the page and try again.`
          );
          setStatusSeverity("error");
          setShowStatus(true);
        }
      });
  };

  return (
    <Container>
      <Title>
        View Patient{" "}
        <Button
          onClick={() => {
            onEditUser();
          }}
        >
          Save Changes
        </Button>
      </Title>

      <Content>
        <ContentTitle>{username}</ContentTitle>

        <ContentBody>
          <ContentInput
            label="First Name"
            variant="standard"
            onChange={(ev) => setFirstName(ev.target.value)}
            value={firstName}
          />
          <br />

          <ContentInput
            label="Last Name"
            variant="standard"
            onChange={(ev) => setLastName(ev.target.value)}
            value={lastName}
          />
          <br />

          <ContentInput
            label="Email"
            variant="standard"
            style={{ marginBottom: "2rem" }}
            onChange={(ev) => setEmail(ev.target.value)}
            value={email}
            disabled
          />
          <br />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Custom input"
              value={birthDate}
              onChange={(newValue: Date | null) => {
                setBirthDate(newValue ? newValue : new Date());
              }}
              renderInput={({ inputRef, inputProps, InputProps }) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    ref={inputRef}
                    {...inputProps}
                    style={{
                      paddingBottom: "10px",
                      border: "none",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.5)",
                    }}
                  />
                  {InputProps?.endAdornment}
                </Box>
              )}
            />
          </LocalizationProvider>
          <br />

          <ContentInputPassword
            label="Password"
            onChange={(ev: any) => setPassword(ev.target.value)}
            style={{ marginTop: "0.5rem !important" }}
          />
          <br />

          <ContentInputPassword
            onChange={(ev: any) => setConfirmPassword(ev.target.value)}
            label="Confirm Password"
          />
          <br />

          <ContentInputProfilePhoto onClick={onImageChangeClick}>
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img src={logo} alt="Click here to upload a profile photo" />
            <ContentInputProfilePhotoIcon />
            <input
              type="file"
              ref={hiddenLogoInput}
              onChange={onHiddenLogoInputChange}
              style={{ display: "none" }}
            />
          </ContentInputProfilePhoto>
        </ContentBody>

        <Select
          placeholder="Language"
          startDecorator={<TranslateIcon />}
          sx={{ width: 240, marginTop: "30px", marginLeft: "30px" }}
          value={language}
          onChange={(e, newValue) => setLanguage(newValue || "")}
        >
          <Option value="pt-PT">Português (Portugal)</Option>
          <Option value="en-US">English (United States)</Option>
        </Select>
      </Content>
      {/*<div style={{ height: 500, width: '100%', paddingTop: "1rem" }}>

    </div>*/}

      <Snackbar
        open={showStatus}
        autoHideDuration={6000}
        onClose={handleCloseStatus}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Users;
